import './style.modules.scss'

import React, { MouseEvent, useRef, useState } from 'react'
import xss from 'xss'

export type QuizAnswer = {
  label: string
  percentage?: string
  content?: string
  linkUrl?: string
  linkTarget?: string
  linkIcon?: string
  linkText?: string
}

export type QuizProps = {
  superheadline: string
  headline: string
  question: string
  content: string
  answers: { answer: QuizAnswer[] }
  percentage: string | number
  linkUrl?: string
  linkTarget?: string
  linkIcon?: string
  linkText?: string
  testId?: string
  className?: string
}

export function Quiz(props: QuizProps) {
  const {
    content,
    superheadline,
    headline,
    question,
    answers,
    percentage,
    linkUrl,
    linkTarget,
    linkIcon,
    linkText,
    testId
  } = props

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const bigNumberRef = useRef<HTMLElement | null>(null)
  const answerBodyRef = useRef<HTMLElement | null>(null)

  const actionButton =
    typeof selectedAnswer === 'number' && !Number.isNaN(selectedAnswer) ? (
      (() => {
        const selectedAnswerData = answers.answer[selectedAnswer]

        return (
          <div className="Content">
            <div
              className="big-number"
              data-testid={testId ? `${testId}-big-number` : undefined}
            >
              <div className="Quiz-percentage u-textColorPurple u-textSolid u-marginBottom16">
                {selectedAnswerData.percentage}
              </div>
            </div>
            {selectedAnswerData.content && (
              <p
                className="u-marginBottom24 Quiz-answerBody is-animated"
                dangerouslySetInnerHTML={{
                  __html: xss(selectedAnswerData.content)
                }}
                data-testid={testId ? `${testId}-answer-body` : undefined}
              ></p>
            )}
            {selectedAnswerData.linkUrl && (
              <a
                href={selectedAnswerData.linkUrl}
                className="Button Button--primary quiz-anim quiz-anim-Active"
                target={selectedAnswerData.linkTarget}
                rel="noopener noreferrer"
                data-testid={testId ? `${testId}-cta-button` : undefined}
              >
                {selectedAnswerData.linkIcon}
                {selectedAnswerData.linkText}
              </a>
            )}
          </div>
        )
      })()
    ) : (
      <div className="Content">
        <div
          className="big-number"
          data-testid={testId ? `${testId}-big-number` : undefined}
        >
          <div className="Quiz-percentage u-textColorPurple u-textSolid u-marginBottom16">
            {percentage}
          </div>
        </div>
        <p
          className="u-marginBottom24 Quiz-answerBody is-animated"
          data-testid={testId ? `${testId}-answer-body` : undefined}
          dangerouslySetInnerHTML={{ __html: content }}
        ></p>
        {linkUrl ? (
          <a
            href={linkUrl}
            className="Button Button--primary quiz-anim quiz-anim-Active"
            target={linkTarget}
            rel="noopener noreferrer"
            data-testid={testId ? `${testId}-cta-button` : undefined}
          >
            {linkIcon}
            {linkText}
          </a>
        ) : null}
      </div>
    )

  function clickAnswer(event: MouseEvent<HTMLButtonElement>, index: number) {
    event.preventDefault()
    setSelectedAnswer(index)
    if (bigNumberRef.current) {
      bigNumberRef.current.classList.remove('u-animFadeInUp')
      // Force reflow between removing and re-adding the animation class to restart the animation
      bigNumberRef.current.getBoundingClientRect()
      bigNumberRef.current.classList.add('u-animFadeInUp')
    }
    if (answerBodyRef.current) {
      answerBodyRef.current.classList.remove('is-animated')
      // Force reflow between removing and re-adding the animation class to restart the animation
      answerBodyRef.current.getBoundingClientRect()
      answerBodyRef.current.classList.add('is-animated')
    }
  }

  return (
    <section className={`u-sizeConstrained`}>
      <div
        className={`Quiz u-flex u-flexRow u-sm-flexCol u-padding0 u-border ${props.className}`}
        data-testid={testId}
      >
        <div
          className="Content Quiz-cardQuestion u-size6of12 u-sm-sizeFull u-bgColorWhite"
          data-analytics-container="Quiz Callout"
        >
          <div
            className="Quiz-superHeader Type-3 u-marginBottom16 u-textUppercase"
            dangerouslySetInnerHTML={{ __html: superheadline }}
            data-testid={testId ? `${testId}-superHeadline` : undefined}
          ></div>
          <div
            className="Quiz-contentHeader Type-3 u-textColorBrand u-marginBottom16"
            dangerouslySetInnerHTML={{ __html: headline }}
            data-testid={testId ? `${testId}-headline` : undefined}
          ></div>
          <div
            className="Quiz-contentHeader Type-4 u-marginBottom20 u-marginTop0"
            dangerouslySetInnerHTML={{ __html: question }}
            data-testid={testId ? `${testId}-question` : undefined}
          ></div>
          <div className="Button-group u-flex">
            {Array.isArray(answers.answer) &&
              answers.answer.map((answerItem, i) => (
                <button
                  key={i}
                  className="Button Button--circular"
                  onClick={event => clickAnswer(event, i)}
                  data-analytics="Stance Button"
                >
                  {answerItem.label}
                </button>
              ))}
          </div>
        </div>
        <div
          className="Quiz-cardAnswer u-flex u-flexAlignItemsCenter u-size6of12 u-sm-sizeFull"
          data-analytics-container="Quiz Answer"
          ref={el => {
            bigNumberRef.current = el ? el.querySelector('.big-number') : null
            answerBodyRef.current = el
              ? el.querySelector('.Quiz-answerBody')
              : null
          }}
        >
          {actionButton}
        </div>
      </div>
    </section>
  )
}
